import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { estudianteService, judicialService } from "@/services/api";

export default function JudicialNuevoPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);
  const [hasDocument, setHasDocument] = useState(false);
  const [students, setStudents] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  
  const [form, setForm] = useState({
    tribunal: "",
    rit: "",
    ruc: "",
    numeroCausa: "",
    tipoRequerimiento: "",
    fechaRecepcion: new Date().toISOString().split("T")[0],
    fechaLimite: "",
    prioridad: "alta" as "baja" | "media" | "alta" | "critica",
    studentId: "",
    observaciones: "",
    documentoNombre: "",
  });

  useEffect(() => {
    async function loadStudents() {
      try {
        const data = await estudianteService.list();
        if (Array.isArray(data)) {
          setStudents(data);
        }
      } catch (err) {
        console.error("Failed to load students:", err);
      }
    }
    loadStudents();
  }, []);

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUploadSimulate = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setHasDocument(true);
      setForm({
        tribunal: "Tribunal de Familia de Santiago",
        rit: "C-4521-2026",
        ruc: "2410987654-3",
        numeroCausa: "C-4521-2026",
        tipoRequerimiento: "Informe integral del estudiante",
        fechaRecepcion: new Date().toISOString().split("T")[0],
        fechaLimite: new Date(Date.now() + 10*24*60*60*1000).toISOString().split("T")[0],
        prioridad: "alta",
        studentId: "s3", // Vicente Díaz Leiva
        observaciones: "Análisis IA completado. Datos extraídos del archivo: " + file.name,
        documentoNombre: file.name
      });
      setStep(2);
    }
  };

  const handleContinueWithoutDocument = () => {
    setHasDocument(false);
    setForm({
      tribunal: "",
      rit: "",
      ruc: "",
      numeroCausa: "",
      tipoRequerimiento: "",
      fechaRecepcion: new Date().toISOString().split("T")[0],
      fechaLimite: "",
      prioridad: "alta",
      studentId: "",
      observaciones: "",
      documentoNombre: ""
    });
    setStep(2);
  };

  const handleSubmit = async () => {
    if (!form.studentId) {
      alert("Por favor seleccione un estudiante.");
      return;
    }
    setSaving(true);

    try {
      await judicialService.create({
        estudiante_id: form.studentId,
        tribunal: form.tribunal || "Tribunal de Familia",
        rit_ruc: form.rit || "C-0000-0000",
        fecha_recepcion: form.fechaRecepcion,
        fecha_vencimiento: form.fechaLimite,
        tipo_requerimiento: form.tipoRequerimiento || "Informe",
        oficio_url: form.documentoNombre || null
      });
      navigate("/judicial");
    } catch (err: any) {
      console.error("Failed to create judicial requirement:", err);
      alert("Error al registrar requerimiento judicial: " + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      {/* Header */}
      <div>
        <button onClick={() => navigate("/judicial")} className="flex items-center gap-1.5 text-sm text-foreground-500 hover:text-foreground-700 transition-colors cursor-pointer mb-3">
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
          Volver a Requerimientos
        </button>
        <h1 className="text-xl md:text-2xl font-bold text-foreground-900 tracking-tight">Nuevo Requerimiento Judicial</h1>
        <p className="text-sm text-foreground-500 mt-1">Registra un oficio o solicitud recibida desde un tribunal</p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${step === 1 ? "bg-primary-50 text-primary-700 border border-primary-200" : "bg-background-50 text-foreground-500 border border-background-200/50"}`}>
          <span className="w-5 h-5 rounded-full bg-primary-500 text-white text-[11px] font-bold flex items-center justify-center">1</span>
          Carga del documento
        </div>
        <div className="w-8 h-px bg-background-300" />
        <div className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${step === 2 ? "bg-primary-50 text-primary-700 border border-primary-200" : "bg-background-50 text-foreground-500 border border-background-200/50"}`}>
          <span className="w-5 h-5 rounded-full bg-foreground-300 text-white text-[11px] font-bold flex items-center justify-center">2</span>
          Datos del requerimiento
        </div>
      </div>

      {/* Step 1: Upload */}
      {step === 1 && (
        <div className="bg-white border border-background-200/70 rounded-xl p-6">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-purple-50 flex items-center justify-center">
              <i className="ri-robot-2-line text-2xl text-purple-500" />
            </div>
            <h2 className="text-lg font-bold text-foreground-900 mb-2">Cargar documento del tribunal</h2>
            <p className="text-sm text-foreground-500 mb-6 max-w-md mx-auto">
              Sube el oficio, resolución o comunicación recibida. La IA analizará automáticamente el documento y extraerá los datos clave.
            </p>

            {/* Upload Zone */}
            <div className="border-2 border-dashed border-background-300 rounded-2xl p-8 mb-6 hover:border-primary-300 transition-colors cursor-pointer bg-background-50/50">
              <div className="w-12 h-12 mx-auto mb-3 rounded-xl bg-primary-50 flex items-center justify-center">
                <i className="ri-upload-cloud-2-line text-xl text-primary-500" />
              </div>
              <p className="text-sm font-medium text-foreground-700 mb-1">Arrastra el archivo aquí o haz clic para seleccionar</p>
              <p className="text-[11px] text-foreground-400">PDF, Word (DOCX) — Máximo 25 MB</p>
            </div>

            <div className="flex items-center justify-center gap-3">
              <label className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft">
                <span className="w-4 h-4 flex items-center justify-center"><i className="ri-file-upload-line" /></span>
                Seleccionar archivo
                <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={handleUploadSimulate} />
              </label>
              <button onClick={handleContinueWithoutDocument} className="flex items-center gap-2 px-4 py-2.5 border border-background-200/70 rounded-xl text-sm font-medium text-foreground-600 hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
                Continuar sin documento
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 2: Form */}
      {step === 2 && (
        <div className="bg-white border border-background-200/70 rounded-xl p-6 space-y-5">
          {/* IA Simulation Banner */}
          {hasDocument && (
            <div className="flex items-start gap-3 p-4 bg-purple-50 border border-purple-200 rounded-xl">
              <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-magic-line text-purple-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-purple-800 mb-1">Análisis IA completado</p>
                <p className="text-xs text-purple-600">
                  La IA ha analizado el documento y ha pre-llenado los campos detectados. Revisa y corrige la información antes de continuar. Los campos marcados con un borde púrpura fueron extraídos automáticamente.
                </p>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Datos del Tribunal</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Tribunal" value={form.tribunal} onChange={(v) => handleChange("tribunal", v)} placeholder="Ej: Tribunal de Familia de Santiago" aiExtracted />
              <FormField label="RIT" value={form.rit} onChange={(v) => handleChange("rit", v)} placeholder="Ej: C-2345-2026" aiExtracted />
              <FormField label="RUC" value={form.ruc} onChange={(v) => handleChange("ruc", v)} placeholder="Ej: 2410123456-7" />
              <FormField label="Número de Causa" value={form.numeroCausa} onChange={(v) => handleChange("numeroCausa", v)} placeholder="Ej: C-2345-2026" />
              <FormField label="Tipo de Requerimiento" value={form.tipoRequerimiento} onChange={(v) => handleChange("tipoRequerimiento", v)} placeholder="Ej: Informe integral del estudiante" aiExtracted />
              <div>
                <label className="block text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Prioridad</label>
                <select value={form.prioridad} onChange={(e) => handleChange("prioridad", e.target.value)} className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-300 cursor-pointer">
                  <option value="critica">Crítica</option>
                  <option value="alta">Alta</option>
                  <option value="media">Media</option>
                  <option value="baja">Baja</option>
                </select>
              </div>
            </div>
          </div>

          <div className="border-t border-background-200/50 pt-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Información del Estudiante</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5 flex items-center gap-2">
                  Estudiante
                  <span className="flex items-center gap-0.5 text-purple-500 text-[10px] font-medium bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200">
                    <i className="ri-magic-line text-[10px]" /> IA
                  </span>
                </label>
                <select
                  value={form.studentId}
                  onChange={(e) => handleChange("studentId", e.target.value)}
                  className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 outline-none focus:border-primary-300 cursor-pointer"
                >
                  <option value="">Seleccionar estudiante...</option>
                  {students.map((s) => (
                    <option key={s.id} value={s.id}>{s.nombre} - {s.curso}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Curso</label>
                <input
                  type="text"
                  readOnly
                  value={students.find(s => s.id === form.studentId || s.id === String(form.studentId))?.curso || ""}
                  placeholder="Se autocompleta al seleccionar estudiante"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-xl text-sm text-foreground-500 outline-none"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-background-200/50 pt-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Plazos</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField label="Fecha de Recepción" value={form.fechaRecepcion} onChange={(v) => handleChange("fechaRecepcion", v)} placeholder="YYYY-MM-DD" aiExtracted />
              <FormField label="Fecha Límite de Respuesta" value={form.fechaLimite} onChange={(v) => handleChange("fechaLimite", v)} placeholder="YYYY-MM-DD" aiExtracted urgent />
            </div>
          </div>

          <div className="border-t border-background-200/50 pt-5">
            <h3 className="text-sm font-bold text-foreground-900 mb-4">Documento y Observaciones</h3>
            <FormField label="Nombre del Documento" value={form.documentoNombre} onChange={(v) => handleChange("documentoNombre", v)} placeholder="Ej: Oficio_Tribunal_Familia_C2345-2026.pdf" />
            <div className="mt-4">
              <label className="block text-[11px] font-bold text-foreground-400 uppercase tracking-wider mb-1.5">Observaciones</label>
              <textarea value={form.observaciones} onChange={(e) => handleChange("observaciones", e.target.value)} rows={3} placeholder="Notas adicionales sobre el requerimiento..." className="w-full px-3 py-2.5 bg-background-50 border border-background-200/70 rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 resize-none" />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-4 border-t border-background-200/50">
            <button onClick={() => setStep(1)} className="flex items-center gap-2 px-4 py-2.5 border border-background-200/70 rounded-xl text-sm font-medium text-foreground-600 hover:bg-background-50 transition-all duration-200 cursor-pointer whitespace-nowrap">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line" /></span>
              Volver
            </button>
            <button onClick={() => navigate("/judicial")} className="px-4 py-2.5 border border-background-200/70 rounded-xl text-sm text-foreground-500 hover:bg-background-50 cursor-pointer whitespace-nowrap">
              Cancelar
            </button>
            <button onClick={handleSubmit} className="ml-auto flex items-center gap-2 px-5 py-2.5 bg-primary-500 text-white rounded-xl text-sm font-semibold hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-soft">
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-check-line" /></span>
              Crear Requerimiento
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function FormField({
  label,
  value,
  onChange,
  placeholder,
  aiExtracted,
  urgent,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  aiExtracted?: boolean;
  urgent?: boolean;
}) {
  return (
    <div>
      <label className={`block text-[11px] font-bold uppercase tracking-wider mb-1.5 flex items-center gap-2 ${urgent ? "text-red-500" : "text-foreground-400"}`}>
        {label}
        {aiExtracted && (
          <span className="flex items-center gap-0.5 text-purple-500 text-[10px] font-medium bg-purple-50 px-1.5 py-0.5 rounded-full border border-purple-200">
            <i className="ri-magic-line text-[10px]" /> IA
          </span>
        )}
        {urgent && <i className="ri-alert-fill text-[10px]" />}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full px-3 py-2.5 bg-background-50 border rounded-xl text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-300 transition-all ${aiExtracted ? "border-purple-300 bg-purple-50/30" : "border-background-200/70"}`}
      />
    </div>
  );
}